/* eslint-disable consistent-return */

import WizardPage from '@gameday/common/components/form/Common/WizardPage'
import RoktProvider from '@gameday/react-components/components/Rokt'
import { isEmpty, isNumber } from 'lodash'
import { useEffect, useState } from 'react'
import Idle from 'react-idle'

import config from '../../../constants/config'
import { privateRoutes } from '../../../constants/routes/routes'
import withColorSetting from '../../../hoc/withColorSettings'
import { convertFormType } from '../../../utils/common'
import AddOnProducts from '../pages/AddOnProducts'
import BasicInfo from '../pages/BasicInfo'
import Participants from '../pages/Participants'
import Payment from '../pages/Payment'
import Products from '../pages/Products'
import Questions from '../pages/Questions'
import SubEvents from '../pages/SubEvents'
import Success from '../pages/Success'
import Summary from '../pages/Summary'
import Teams from '../pages/Teams'

const storedStepKey = '__step'
const isDev = config.NODE_ENV === 'development'

function getStoredStep(defaultStep = -1) {
  if (!isDev) {
    return defaultStep
  }

  const storedStep = sessionStorage.getItem(storedStepKey)
  if (storedStep) {
    return Number(storedStep)
  }

  return defaultStep
}

function useStoredStep(step) {
  if (isDev) {
    sessionStorage.setItem(storedStepKey, step)
  }
}

const Wizard = withColorSetting(WizardPage)

const components = {
  teams: Teams,
  participants: Participants,
  basicInfo: BasicInfo,
  products: Products,
  subEvents: SubEvents,
  addOnProducts: AddOnProducts,
  questions: Questions,
  summary: Summary,
  payment: Payment,
  success: Success,
}

function PrivateRoute({
  signout,
  isAuthenticated,
  history,
  startTimer,
  setStartTimer,
  resetTimer,
  resetForm,
  match,
  setSessionId,
  voucherSessionId,
  resetVoucher,
  formSettings,
  currentRego,
  messages,
  showLoading,
  paymentProviders,
  paymentSuccessful,
  useSubEvents,
  subEvents,
  addOn,
  roktConfiguration,
  isTeamEvent,
  teamFormType,
  isTeamReset,
  formInfo,
  registeredTeam,
  registrations,
}) {
  useEffect(() => {
    if (!isAuthenticated) {
      signout()
    }
    const listener = history.block((_, action) => {
      if (action === 'POP') {
        return 'Your registration will be lost, do you want to continue?'
      }
    })
    return function cleanup() {
      listener()
    }
  }, [])

  const { formType, eventType, eventLogo } = formSettings
  const type = convertFormType(formType) || convertFormType(formInfo?.form?.type)

  const useSub = useSubEvents && formType === 'event' && eventType === 'Event Entry' && !isEmpty(subEvents)

  const useAddOn = !isEmpty(addOn.products)

  const isEvent = type === 'event'
  const isTeamRego = isTeamEvent && teamFormType === 'Register A Team'

  let steps = privateRoutes(isTeamRego)
  // eslint-disable-next-line prefer-const
  const skipCurrentRegoArr = [0, 1, 2, 3, 4, 5]

  let updateComponents = components

  if (!useSub) {
    // eslint-disable-next-line no-shadow
    const { subEvents, ...restComponents } = updateComponents
    updateComponents = restComponents
    steps = [...privateRoutes(isTeamRego).filter(f => f.id !== 'subEvents')]
    skipCurrentRegoArr.pop()
  }

  if (type?.toLowerCase() === 'shop') {
    const { products, ...restComponents } = updateComponents
    updateComponents = restComponents
    steps = [...steps.filter(f => f.id !== 'products')]
    skipCurrentRegoArr.pop()
  }

  if (!useAddOn) {
    const { addOnProducts, ...restComponents } = updateComponents
    updateComponents = restComponents
    steps = [...steps.filter(f => f.id !== 'addOnProducts')]
    skipCurrentRegoArr.pop()
  }

  if (!isTeamEvent) {
    const { teams, ...restComponents } = updateComponents
    updateComponents = restComponents
    steps = [...steps.filter(f => f.id !== 'teams')]
    skipCurrentRegoArr.pop()
  }

  if (isTeamEvent && teamFormType === 'Register A Team') {
    const { participants, basicInfo, ...restComponents } = updateComponents
    updateComponents = restComponents
    steps = [...steps.filter(f => f.id !== 'participants' && f.id !== 'basicInfo')]
    skipCurrentRegoArr.pop()
  }

  const filteredComponents = Object.values(updateComponents)

  if (!isTeamEvent) {
    steps.shift()
  }

  const initStep = isTeamEvent ? 0 : -1

  const [step, setStep] = useState(getStoredStep(initStep))
  const [previousStep, setPreviousStep] = useState(initStep)

  const [idleTimeout, setIdleTimeout] = useState(30 * 60 * 1000)

  const next = () => {
    setPreviousStep(step)
    setStep(Math.min(step + 1, isTeamEvent ? steps.length : steps.length - 1))
  }
  const previous = () => {
    setPreviousStep(step - 1)
    setStep(Math.max(step - 1, -1))
  }
  const goto = path => {
    setPreviousStep(step)
    if (isNumber(path)) {
      return setStep(path)
    }
    const newStep = privateRoutes(isTeamRego).findIndex(s => s.id === path)
    setStep(isTeamEvent ? newStep : newStep - 1)
  }

  const onIdleChange = event => {
    if (event.idle && isAuthenticated) {
      signout()
    }
  }

  const onTimeup = () => {
    setTimeout(() => {
      setStartTimer(false)
      if (voucherSessionId) {
        resetVoucher(voucherSessionId)
      }
      setSessionId(null)
      paymentSuccessful(true)
      goto(initStep)
      resetForm()
      showLoading(false)
    }, 1000)
  }

  useEffect(() => {
    const newTime = startTimer ? 31 : 30
    setIdleTimeout(newTime * 60000)
  }, [startTimer, voucherSessionId])

  useEffect(() => {
    if (isAuthenticated && skipCurrentRegoArr.includes(step)) {
      if (!currentRego && !isTeamEvent) {
        goto(initStep)
      }
    }
  }, [step])

  if (isAuthenticated && isTeamEvent && (step === -1 || (isTeamReset && step === 3))) {
    goto(initStep)
  }
  useStoredStep(step)

  const { enableRoktMembershipForm, enableRoktEventForm, enableRoktShopForm } = roktConfiguration || {}
  let enableRokt = false

  if (formType === 'membership') {
    enableRokt = enableRoktMembershipForm
  }
  if (formType === 'event') {
    enableRokt = enableRoktEventForm
  }
  if (formType === 'shop') {
    enableRokt = enableRoktShopForm
  }

  return (
    <>
      <RoktProvider enabled={enableRokt}>
        <Wizard
          Component={filteredComponents[isTeamEvent ? step : step + 1]}
          logOutRequest={signout}
          steps={steps}
          currentStep={step}
          previousStep={previousStep}
          next={next}
          previous={previous}
          goto={goto}
          startTimer={startTimer}
          resetTimer={resetTimer}
          onTimeup={onTimeup}
          orgLogo={formSettings.orgLogo}
          headerColour={formSettings.headerBackgroundColour}
          headerTextColour={formSettings.headerTextColour}
          buttonColour={formSettings.buttonColour}
          buttonTextColour={formSettings.buttonTextColour}
          textLinkColour={formSettings.textLinkColour}
          match={match}
          history={history}
          messages={messages}
          paymentGateway={paymentProviders}
          hasSubEvents={useSub}
          isEvent={isEvent}
          isTeamEvent={isTeamEvent}
          teamFormType={teamFormType}
          eventLogo={eventLogo}
          formInfo={formInfo}
          subdomain={formSettings.subdomain}
          resetForm={resetForm}
          resetVoucher={resetVoucher}
          teamData={registeredTeam}
          registrations={registrations}
          formSettings={formSettings}
        />
      </RoktProvider>
      <Idle timeout={idleTimeout} onChange={onIdleChange} events={['mousedown', 'keydown', 'touchstart', 'scroll']} />
    </>
  )
}

export default PrivateRoute
